import type { ApiResponse, PageResponse } from "../../../shared/types/api";
import { httpClient } from "../http-client";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";
import type {
  BusinessClient,
  BusinessClientDetail,
  OrderFile,
  ManagedClient,
  ManagedClientDetail,
  CreateManagedClientRequest,
  UpdateManagedClientRequest,
  CompareResult,
} from "../../../domain/types/business-client";

export class BusinessClientApiService {
  async getBusinessClients(
    companyId: number,
    keyword?: string,
    page?: number,
    size?: number,
  ): Promise<ApiResponse<PageResponse<BusinessClient>>> {
    const params: Record<string, any> = {};
    if (keyword) params.keyword = keyword;
    if (page !== undefined) params.page = page;
    if (size !== undefined) params.size = size;

    const response = await httpClient.get<ApiResponse<PageResponse<BusinessClient>>>(
      NEW_API_ENDPOINTS.companyBusinessClientController.GET.list(companyId),
      { params },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "기업고객 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getBusinessClientDetail(
    companyId: number,
    userId: number,
  ): Promise<ApiResponse<BusinessClientDetail>> {
    const response = await httpClient.get<ApiResponse<BusinessClientDetail>>(
      NEW_API_ENDPOINTS.companyBusinessClientController.GET.detail(companyId, userId),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "기업고객 상세 정보를 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getBusinessClientOrders(
    companyId: number,
    userId: number,
    params?: { status?: string; dateFrom?: string; dateTo?: string; page?: number; size?: number },
  ): Promise<ApiResponse<PageResponse<any>>> {
    const response = await httpClient.get<ApiResponse<PageResponse<any>>>(
      NEW_API_ENDPOINTS.companyBusinessClientController.GET.orders(companyId, userId),
      { params },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "기업고객 주문내역을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async getBusinessClientOrderFiles(
    companyId: number,
    userId: number,
    page?: number,
    size?: number,
  ): Promise<ApiResponse<PageResponse<OrderFile>>> {
    const params: Record<string, any> = {};
    if (page !== undefined) params.page = page;
    if (size !== undefined) params.size = size;

    const response = await httpClient.get<ApiResponse<PageResponse<OrderFile>>>(
      NEW_API_ENDPOINTS.companyBusinessClientController.GET.orderFiles(companyId, userId),
      { params },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "기업고객 주문 파일 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  // ============ 거래처 관리 (partners) ============

  async getManagedClients(
    companyId: number, keyword?: string, page?: number, size?: number,
  ): Promise<ApiResponse<PageResponse<ManagedClient>>> {
    const params: Record<string, any> = {};
    if (keyword) params.keyword = keyword;
    if (page !== undefined) params.page = page;
    if (size !== undefined) params.size = size;
    const response = await httpClient.get<ApiResponse<PageResponse<ManagedClient>>>(
      NEW_API_ENDPOINTS.companyManagedClientController.GET.list(companyId), { params },
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "거래처 목록을 가져오지 못했습니다.");
    return response.data;
  }

  async getManagedClientDetail(companyId: number, id: number): Promise<ApiResponse<ManagedClientDetail>> {
    const response = await httpClient.get<ApiResponse<ManagedClientDetail>>(
      NEW_API_ENDPOINTS.companyManagedClientController.GET.detail(companyId, id),
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "거래처 상세 정보를 가져오지 못했습니다.");
    return response.data;
  }

  async createManagedClient(companyId: number, body: CreateManagedClientRequest): Promise<ApiResponse<ManagedClient>> {
    const response = await httpClient.post<ApiResponse<ManagedClient>>(
      NEW_API_ENDPOINTS.companyManagedClientController.POST.create(companyId), body,
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "거래처 등록에 실패했습니다.");
    return response.data;
  }

  async updateManagedClient(companyId: number, id: number, body: UpdateManagedClientRequest): Promise<ApiResponse<ManagedClient>> {
    const response = await httpClient.put<ApiResponse<ManagedClient>>(
      NEW_API_ENDPOINTS.companyManagedClientController.PUT.update(companyId, id), body,
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "거래처 수정에 실패했습니다.");
    return response.data;
  }

  async deleteManagedClient(companyId: number, id: number): Promise<ApiResponse<void>> {
    const response = await httpClient.delete<ApiResponse<void>>(
      NEW_API_ENDPOINTS.companyManagedClientController.DELETE.delete(companyId, id),
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "거래처 삭제에 실패했습니다.");
    return response.data;
  }

  async linkUser(companyId: number, id: number, userId: number): Promise<ApiResponse<ManagedClient>> {
    const response = await httpClient.put<ApiResponse<ManagedClient>>(
      NEW_API_ENDPOINTS.companyManagedClientController.PUT.linkUser(companyId, id), { userId },
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "유저 연결에 실패했습니다.");
    return response.data;
  }

  async unlinkUser(companyId: number, id: number): Promise<ApiResponse<ManagedClient>> {
    const response = await httpClient.put<ApiResponse<ManagedClient>>(
      NEW_API_ENDPOINTS.companyManagedClientController.PUT.unlinkUser(companyId, id),
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "유저 연결 해제에 실패했습니다.");
    return response.data;
  }

  async getManagedClientOrders(
    companyId: number,
    id: number,
    params?: { status?: string; dateFrom?: string; dateTo?: string; page?: number; size?: number },
  ): Promise<ApiResponse<PageResponse<any>>> {
    const response = await httpClient.get<ApiResponse<PageResponse<any>>>(
      NEW_API_ENDPOINTS.companyManagedClientController.GET.orders(companyId, id),
      { params },
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "거래처 주문내역을 가져오지 못했습니다.");
    return response.data;
  }

  async getManagedClientOrderFiles(
    companyId: number,
    id: number,
    page?: number,
    size?: number,
  ): Promise<ApiResponse<PageResponse<OrderFile>>> {
    const params: Record<string, any> = {};
    if (page !== undefined) params.page = page;
    if (size !== undefined) params.size = size;
    const response = await httpClient.get<ApiResponse<PageResponse<OrderFile>>>(
      NEW_API_ENDPOINTS.companyManagedClientController.GET.orderFiles(companyId, id),
      { params },
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "거래처 주문 파일 목록을 가져오지 못했습니다.");
    return response.data;
  }

  async compareManagedClient(companyId: number, id: number): Promise<ApiResponse<CompareResult>> {
    const response = await httpClient.get<ApiResponse<CompareResult>>(
      NEW_API_ENDPOINTS.companyManagedClientController.GET.compare(companyId, id),
    );
    if (response.data.errorCode) throw new Error(response.data.errorMsg || "비교 정보를 가져오지 못했습니다.");
    return response.data;
  }
}

export const businessClientApiService = new BusinessClientApiService();